import type { CurrentSet, CurrentState, CurrentPhase } from '../sources/currents.ts';

/**
 * Turning Golden Gate current predictions into the one thing a Bay sailor
 * actually decides on: is the wind fighting the stream?
 *
 * The summer sea breeze comes through the Gate from the WSW, blowing toward
 * roughly 050°. The ebb sets 257°, almost exactly into it — which is why an
 * ebb against the westerly stacks up steep, short chop, and why the same wind
 * over a flood (setting 069°) stays comparatively smooth.
 */

/** Within this of a slack event, call it slack rather than a weak stream. */
const SLACK_WINDOW_MIN = 20;

/** Below this the stream is too weak to raise a sea whatever the wind does. */
export const SIGNIFICANT_CURRENT_KT = 1.5;

/** Below this the wind cannot build meaningful chop against any stream. */
export const SIGNIFICANT_WIND_KT = 12;

export function currentStateAt(set: CurrentSet, now: Date = new Date()): CurrentState | null {
  const { events } = set;
  if (events.length === 0) return null;

  const t = now.getTime();
  const prev = [...events].reverse().find((e) => e.t.getTime() <= t) ?? null;
  const next = events.find((e) => e.t.getTime() > t) ?? null;
  if (!prev && !next) return null;

  const nextSlack = events.find((e) => e.kind === 'slack' && e.t.getTime() > t)?.t ?? null;

  // The governing max is whichever side of now is not a slack.
  const governing =
    prev && prev.kind !== 'slack' ? prev : next && next.kind !== 'slack' ? next : null;

  // Sitting on a slack event, or bracketed by two slacks with no max between.
  const nearestSlackGap = Math.min(
    ...[prev, next]
      .filter((e): e is NonNullable<typeof e> => e !== null && e.kind === 'slack')
      .map((e) => Math.abs(e.t.getTime() - t)),
    Number.POSITIVE_INFINITY,
  );

  if (!governing || nearestSlackGap <= SLACK_WINDOW_MIN * 60_000) {
    return {
      phase: 'slack',
      trend: 'easing',
      peakKt: 0,
      peakAt: (governing ?? prev ?? next)!.t,
      nextSlack,
      setDeg: governing?.velocityKt !== undefined && governing.velocityKt < 0
        ? set.ebbDir
        : set.floodDir,
    };
  }

  const phase: CurrentPhase = governing.velocityKt < 0 ? 'ebb' : 'flood';

  return {
    phase,
    // If the max is still ahead of us the stream is making; if behind, easing.
    trend: governing.t.getTime() > t ? 'building' : 'easing',
    peakKt: Math.abs(governing.velocityKt),
    peakAt: governing.t,
    nextSlack,
    setDeg: phase === 'ebb' ? set.ebbDir : set.floodDir,
  };
}

export type WindCurrentRelation = 'opposed' | 'cross' | 'aligned';

/** Smallest angle between two bearings, 0–180. */
export function angleBetween(a: number, b: number): number {
  return Math.abs((((a - b) % 360) + 540) % 360 - 180);
}

/**
 * @param windFromDeg Direction the wind blows FROM, as every station reports it.
 * @param setDeg      Direction the stream sets TOWARD.
 */
export function windVsCurrent(windFromDeg: number, setDeg: number): {
  relation: WindCurrentRelation;
  angle: number;
} {
  const windToward = (windFromDeg + 180) % 360;
  const angle = angleBetween(windToward, setDeg);
  const relation: WindCurrentRelation =
    angle > 135 ? 'opposed' : angle < 45 ? 'aligned' : 'cross';
  return { relation, angle };
}

export interface Advisory {
  relation: WindCurrentRelation;
  phase: CurrentPhase;
  /** True only when the combination is strong enough to actually matter. */
  notable: boolean;
  text: string;
}

/**
 * The plain-language verdict. Deliberately silent unless both the wind and the
 * stream are strong enough to be worth mentioning — a permanent banner that
 * says "wind against ebb" at 4 knots of breeze trains people to ignore it.
 */
export function advisoryFor(
  windFromDeg: number | null,
  windKt: number,
  state: CurrentState | null,
): Advisory | null {
  if (windFromDeg === null || !state || state.phase === 'slack') return null;

  const { relation } = windVsCurrent(windFromDeg, state.setDeg);
  const notable =
    relation === 'opposed' &&
    state.peakKt >= SIGNIFICANT_CURRENT_KT &&
    windKt >= SIGNIFICANT_WIND_KT;

  const text = notable
    ? `Wind against the ${state.phase} — expect steep, short chop`
    : relation === 'aligned'
      ? `Wind with the ${state.phase} — comparatively smooth`
      : relation === 'opposed'
        ? `Wind against the ${state.phase}, but both are light`
        : `Wind across the ${state.phase}`;

  return { relation, phase: state.phase, notable, text };
}
